import {getFirestore, collection, doc, updateDoc} from 'firebase/firestore';
import {FormatHelper} from '../services/formatHelper';
import {IUserService} from '../services/user';
import {getElements, setOnClick, setText} from '../services/html';
import {HTML_ATTR_REPLACE_VAR, HTML_ATTR_ACTION} from '../const/html';
import {ITrackingService} from '../services/gtmHelper';
import {HtmlHelper} from '../services/htmlHelper';
import {BLUEPRINT_VARIANT} from '../const/queryParameters';
import {AuthService} from '../services/authService';
import {EnvConfig} from '../config/envConfig.interface';
import {IBindingService} from '../services/bindingService';

export abstract class Page {
  public abstract render(): void;

  public renderPre(): void {
    this.bindGeneral();
    this.trackGeneral();
  }

  constructor(
    protected configuration: EnvConfig,
    protected authService: AuthService,
    protected bindingService: IBindingService,
    protected userService: IUserService,
    protected trackingService: ITrackingService,
  ) {}

  private bindGeneral(): void {
    const variant =
      HtmlHelper.instance.getUrlQueryParam(BLUEPRINT_VARIANT) ?? null;

    const userLoggedIn = this.bindingService.getElementsForLoggedInUserOnly();
    const userDetailsMail = getElements(
      `[${HTML_ATTR_REPLACE_VAR}='user-mail']`,
    );
    const userDetailsProvider = getElements(
      `[${HTML_ATTR_REPLACE_VAR}='user-provider']`,
    );
    const userLogoutBtn = getElements(`[${HTML_ATTR_ACTION}='user-logout']`);
    // we use tilde here which allows to query for data attributes containing multiple values
    // e.g. data-sc-render="variant-3 variant-4".
    // we assume that initially all the conditional elements have the hide class applied
    const availableForVariant =
      this.bindingService.getElementsAvailableForVariant(variant);

    // hide per default on pageload
    for (const forLoggedInUserOnly of userLoggedIn) {
      HtmlHelper.instance.hideElement(forLoggedInUserOnly);
    }

    for (const elementForVariant of availableForVariant) {
      HtmlHelper.instance.showElement(elementForVariant);
    }

    for (const elem of userLogoutBtn) {
      setOnClick(elem, () => {
        void this.authService.signOut();
      });
    }

    this.authService.onAuthStateChanged(async user => {
      const isAnonymous = await this.userService.isAnonymous();
      for (const forLoggedInUserOnly of userLoggedIn) {
        if (user === null || isAnonymous) {
          HtmlHelper.instance.hideElement(forLoggedInUserOnly);
        } else {
          HtmlHelper.instance.showElement(forLoggedInUserOnly);
        }
      }

      for (const elem of userDetailsMail) {
        setText(elem, user?.email || '');
      }

      for (const elem of userDetailsProvider) {
        const friendly = FormatHelper.instance.authProviderFriendlyName(
          user?.providerData[0]?.providerId,
        );
        if (friendly) {
          setText(elem, `via ${friendly}`);
        } else {
          setText(elem, '');
        }
      }
    });
  }

  private trackGeneral(): void {
    this.authService.onAuthStateChanged(async user => {
      const isValid = user !== null && user?.isAnonymous === false;

      const userData: {
        userId: string | null;
        name: string | null;
        email: string | null;
        signUpTime: string | null;
        services: {
          intercom: {
            userHash: string | null;
          };
        };
      } = {
        userId: null,
        name: null,
        email: null,
        signUpTime: null,
        services: {
          intercom: {
            userHash: null,
          },
        },
      };

      if (isValid && user) {
        const userDoc = await this.userService.currentUser();

        if (userDoc !== null) {
          userData.userId = user.uid;
          userData.email = userDoc.email ?? null;
          userData.name = userDoc.firstname + ' ' + userDoc.lastname;
          userData.signUpTime = user.metadata.creationTime ?? null;
          userData.services.intercom.userHash =
            userDoc.intercomToken?.web ?? null;
        }
      }

      this.trackingService.trackEvent('userchanged', {
        user: userData,
      });
    });
  }

  protected alwaysForceUser(): Promise<void> {
    return new Promise<void>(resolve => {
      this.authService.onAuthStateChanged(async user => {
        if (user === null) {
          console.log('logging in anonymously...');
          await this.authService.signInAnonymously();
          resolve();
          return;
        }

        const isAnonymous = await this.userService.isAnonymous();
        if (!isAnonymous) {
          if (user.metadata.creationTime === user.metadata.lastSignInTime) {
            const firestore = getFirestore();
            const languageAreaIdFromConfig =
              this.configuration.languageArea.firestoreId;
            const languageArea = doc(
              collection(firestore, 'languageAreas'),
              languageAreaIdFromConfig,
            );
            // we don't need to wait for this, because it's not necessary for the user to see the page
            void updateDoc(doc(collection(firestore, 'users'), user.uid), {
              _shouldUpdate: true,
              languageArea,
            });
            console.log('triggered register finalization');
          }
        }
        resolve();
      });
    });
  }
}
